import React, { useContext, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../hooks/ThemeContext";
import { ExternalLink, ImageOff, ArrowRight } from "lucide-react";
import { api } from "../services/baseUrl";

/* ── URL builder ────────────────────────────────────────────────────────────── */
const BASE_API =
  process.env.REACT_APP_API_BASE_URL ||
  api?.defaults?.baseURL ||
  "https://api.krayneagency.fr/api";
const API_ROOT = BASE_API.replace(/\/api\/?$/, "/");

const buildImageUrl = (img) => {
  if (!img) return null;
  let path =
    typeof img === "object"
      ? (img.path_watermarked ?? img.path_image ?? img.image_url ?? "")
      : String(img);
  if (!path) return null;
  if (path.startsWith("http")) path = path.replace(/^https?:\/\/[^/]+\//, "");
  path = path.replace(/^\/+/, "");
  if (path.startsWith("storage/")) path = path.slice(8);
  if (path.startsWith("api/images/")) path = path.slice(11);
  return `${API_ROOT}api/images/${path}`;
};

const normalizeProjectUrl = (value) => {
  const text = String(value || "").trim();
  if (!text) return null;
  return /^https?:\/\//i.test(text) ? text : `https://${text}`;
};

/* ── Image protégée (data URI — pas de téléchargement direct) ──────────────── */
function ProtectedImg({ src, alt, className }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!src) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setDataUrl(null);

    (async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(src, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        if (cancelled) return;
        const reader = new FileReader();
        reader.onloadend = () => {
          if (!cancelled) {
            setDataUrl(reader.result);
            setStatus("ok");
          }
        };
        reader.readAsDataURL(blob);
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [src]);

  if (status === "loading")
    return <div className="w-full h-full animate-pulse bg-white/[0.06]" />;
  if (status === "error" || !dataUrl) return null;

  return (
    <img
      src={dataUrl}
      alt={alt}
      className={className}
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        userSelect: "none",
        pointerEvents: "none",
        WebkitUserDrag: "none",
      }}
    />
  );
}

/* ── Carte projet ───────────────────────────────────────────────────────────── */
const ACCENTS = [
  "#f43f5e",
  "#a855f7",
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#06b6d4",
];

function ProjectCard({ project, isDark }) {
  const images = (project.images || []).map(buildImageUrl).filter(Boolean);

  const [idx] = useState(0);

  const accent = ACCENTS[(project.id_product ?? 0) % ACCENTS.length];
  const catName =
    project.category?.name_category ??
    project.category?.name ??
    (typeof project.category === "string" ? project.category : null);

  return (
    <Link
      to={`/realisation/${project.id_product || project.id}`}
      className="block group flex flex-col rounded-2xl border overflow-hidden transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
      tabIndex={0}
    >
      {/* ...existing code... */}
      <article className={`flex flex-col flex-1 h-full`}>
        {/* Zone image */}
        <div
          className="relative w-full overflow-hidden select-none"
          style={{ aspectRatio: "16/9" }}
        >
          {images.length > 0 ? (
            <>
              <div className="w-full h-full bg-black/20">
                <ProtectedImg
                  src={images[idx]}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {images.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 pointer-events-none">
                  {images.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        i === idx ? "w-4 bg-white" : "w-1.5 bg-white/40"
                      }`}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div
              className={`w-full h-full flex items-center justify-center ${
                isDark ? "bg-white/[0.04]" : "bg-gray-50"
              }`}
            >
              <ImageOff className="w-10 h-10 text-gray-500" />
            </div>
          )}
        </div>
        {/* Contenu */}
        <div className="flex flex-col gap-3 p-5 flex-1">
          {catName && (
            <span
              className="text-[10px] font-black uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border w-fit"
              style={{
                color: accent,
                borderColor: accent + "40",
                backgroundColor: accent + "18",
              }}
            >
              {catName}
            </span>
          )}
          <div className="flex-1 flex flex-col gap-1.5">
            <h3
              className={`font-bold text-base leading-snug ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {project.title}
            </h3>
            {project.description && (
              <p
                className={`text-sm leading-relaxed line-clamp-3 ${
                  isDark ? "text-gray-400" : "text-zinc-600"
                }`}
              >
                {project.description}
              </p>
            )}
          </div>
          {project.project_link && (
            <a
              href={project.project_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-auto inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
              style={{ color: accent }}
            >
              Voir le projet <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </article>
    </Link>
  );
}

/* ── Page principale ────────────────────────────────────────────────────────── */
const Portfolio = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const g = "linear-gradient(135deg, #f43f5e 0%, #a855f7 50%, #6366f1 100%)";

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    api
      .get("/realisations")
      .then((res) => {
        const raw = Array.isArray(res.data)
          ? res.data
          : (res.data?.data ?? res.data?.products ?? []);
        setProjects(
          raw.map((p) => ({
            id_product: p.id_realisation ?? p.id,
            title: p.title ?? p.name ?? "",
            description: p.description ?? p.short_description ?? "",
            category: p.category ?? null,
            images: Array.isArray(p.images)
              ? p.images
              : p.main_image
                ? [p.main_image]
                : [],
            project_link: normalizeProjectUrl(
              p.project_url ?? p.project_link ?? p.link ?? null,
            ),
          })),
        );
      })
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  /* Catégories uniques */
  const categories = [
    "all",
    ...Array.from(
      new Set(
        projects
          .map(
            (p) =>
              p.category?.name_category ??
              p.category?.name ??
              (typeof p.category === "string" ? p.category : null),
          )
          .filter(Boolean),
      ),
    ),
  ];

  const filtered =
    activeCategory === "all"
      ? projects
      : projects.filter((p) => {
          const cn =
            p.category?.name_category ??
            p.category?.name ??
            (typeof p.category === "string" ? p.category : null);
          return cn === activeCategory;
        });

  return (
    <div
      className="flex-1 flex flex-col"
      style={{ backgroundColor: isDark ? "#07070a" : "#ffffff" }}
    >
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-28 pb-20 px-6 mt-7">
        <div
          className="pointer-events-none absolute top-[-120px] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full opacity-[0.06] blur-3xl"
          style={{ background: g }}
        />
        <div className="relative max-w-3xl mx-auto text-center mt-10">
          <h1
            className={`text-4xl md:text-5xl font-black leading-[1.1] mb-5 ${
              isDark ? "text-white" : "text-gray-900"
            }`}
            style={{ letterSpacing: "-0.03em" }}
          >
            Mes{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: g }}
            >
              réalisations
            </span>
          </h1>
          <p
            className={`text-lg leading-relaxed max-w-xl mx-auto ${
              isDark ? "text-gray-400" : "text-zinc-600"
            }`}
          >
            Découvrez les projets que j'ai créés — design, développement et
            création digitale sur-mesure.
          </p>
        </div>
      </section>

      {/* ── Filtres catégories ────────────────────────────────────────────── */}
      {!loading && categories.length > 1 && (
        <section className="px-6 pb-8">
          <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
            {categories.map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 border ${
                    active
                      ? "text-white border-transparent"
                      : isDark
                        ? "text-gray-400 border-white/[0.08] hover:text-white hover:bg-white/[0.04]"
                        : "text-zinc-600 border-gray-200 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  style={
                    active
                      ? { backgroundImage: g, borderColor: "transparent" }
                      : {}
                  }
                >
                  {cat === "all" ? "Tous les projets" : cat}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Grille ───────────────────────────────────────────────────────── */}
      <section className="px-6 pb-24 flex-1">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`rounded-2xl border overflow-hidden ${
                    isDark
                      ? "border-white/[0.08] bg-white/[0.03]"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <div
                    className="w-full animate-pulse bg-white/[0.06]"
                    style={{ aspectRatio: "16/9" }}
                  />
                  <div className="p-5 flex flex-col gap-3">
                    <div
                      className={`h-3 w-20 rounded-full animate-pulse ${isDark ? "bg-white/10" : "bg-gray-200"}`}
                    />
                    <div
                      className={`h-4 w-3/4 rounded animate-pulse ${isDark ? "bg-white/10" : "bg-gray-200"}`}
                    />
                    <div
                      className={`h-3 w-full rounded animate-pulse ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    />
                    <div
                      className={`h-3 w-2/3 rounded animate-pulse ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className={`rounded-3xl border overflow-hidden ${isDark ? "border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01]" : "border-gray-200 bg-gradient-to-br from-gray-50 to-white"} p-12 md:p-20`}>
              <div className="max-w-2xl mx-auto text-center space-y-8">
                {/* Icon */}
                <div className="flex justify-center">
                  <div
                    className="h-20 w-20 rounded-2xl flex items-center justify-center"
                    style={{
                      background: "linear-gradient(135deg,#f43f5e18,#a855f718)",
                    }}
                  >
                    <ImageOff className="w-10 h-10 text-rose-400" />
                  </div>
                </div>

                {/* Titre */}
                <div>
                  <h2 className={`text-3xl md:text-4xl font-black leading-[1.2] mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
                    {activeCategory === "all"
                      ? "Les réalisations arrivent bientôt 🎨"
                      : "Aucun projet dans cette catégorie"}
                  </h2>
                  <p className={`text-base md:text-lg leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {activeCategory === "all"
                      ? "Je suis en train de créer et de documenter mes derniers projets. Revenez bientôt pour découvrir un portfolio complet de design, développement et création digitale sur-mesure."
                      : "Explorez d'autres catégories ou réessayez plus tard pour voir les nouveaux projets."}
                  </p>
                </div>

                {/* CTA */}
                {activeCategory !== "all" && (
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => setActiveCategory("all")}
                      className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${isDark ? "bg-white/10 hover:bg-white/15 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-900"}`}
                    >
                      Voir tous les projets
                    </button>
                  </div>
                )}

                {/* Stats placeholder */}
                <div className="pt-8 border-t border-white/10">
                  <div className="grid grid-cols-3 gap-6 max-w-sm mx-auto">
                    <div>
                      <div className={`text-2xl font-black mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                        ∞
                      </div>
                      <p className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
                        Idées créatives
                      </p>
                    </div>
                    <div>
                      <div className={`text-2xl font-black mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                        100%
                      </div>
                      <p className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
                        Sur-mesure
                      </p>
                    </div>
                    <div>
                      <div className={`text-2xl font-black mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>
                        🚀
                      </div>
                      <p className={`text-xs ${isDark ? "text-white/60" : "text-gray-500"}`}>
                        Bientôt
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project) => (
                <ProjectCard
                  key={project.id_product}
                  project={project}
                  isDark={isDark}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto">
          <div
            className="relative rounded-3xl p-10 text-center overflow-hidden mb-3"
            style={{ backgroundImage: g }}
          >
            <div
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{
                background:
                  "radial-gradient(ellipse at 80% 20%, #ffffff 0%, transparent 70%)",
              }}
            />
            <h2
              className="relative text-2xl md:text-3xl font-black text-white mb-3"
              style={{ letterSpacing: "-0.03em" }}
            >
              Vous avez un projet en tête&nbsp;?
            </h2>
            <p className="relative text-white/80 text-base mb-7 max-w-md mx-auto">
              Discutons de votre vision et créons quelque chose d'exceptionnel
              ensemble.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 mb-3 rounded-xl bg-white text-gray-900 font-bold text-sm transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              Démarrer un projet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
